import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardBody,
  Container,
  Heading,
  Text,
  SimpleGrid,
  Flex,
  Button,
  Icon,
  Divider,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  FaBus,
  FaWallet,
  FaClock,
  FaCheckCircle,
  FaMapMarkerAlt,
} from "react-icons/fa";
import Modal_Pay_bus from "./Modal_Pay_bus";
import { BASE_URL } from "../../../Globalapi/Globalapi";

const BusFeeDashboard = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [busInfo, setBusInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [loadingState, setLoadingState] = useState(true);
  const [transportInfo, setTransportInfo] = useState(null);

  useEffect(() => {
    const fetchBusData = async () => {
      const studentId = sessionStorage.getItem("student_id");
      const schoolId = sessionStorage.getItem("school_id");

      if (!studentId || !schoolId) {
        setError("Missing required identifiers");
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `${BASE_URL}/student/bus/bus_details.php`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              student_id: studentId,
              school_id: schoolId,
            }),
          }
        );

        const data = await response.json(); // Parse JSON response
        console.log("a", data);

        if (!response.ok || !data.success) {
          // Handle both HTTP errors and API-reported errors
          setError(data.message || `Failed to fetch: ${response.statusText}`);
          setIsLoading(false);
          return;
        }

        if (data.success) {
          const fetchedData = data.data;
          setBusInfo({
            departure: fetchedData.start_place,
            destination: fetchedData.end_place,
            pickupPoint: fetchedData.pickup_location_name,
            dropPoint: fetchedData.drop_location_name,
            fee: parseFloat(fetchedData.bus_fee) || 0, // Ensure fee is a number
            startDate: fetchedData.start_date,
            endDate: fetchedData.end_date,
            dueDate: fetchedData.due_date,
            id: fetchedData.id,
          });
        }
      } catch (error) {
        setError("Error occurred while fetching bus data");
      } finally {
        setIsLoading(false);
      }
    };

    fetchBusData();
  }, []);

  useEffect(() => {
    const loadBusDetails = async () => {
      const currentStudentId = sessionStorage.getItem("student_id");
      const currentSchoolId = sessionStorage.getItem("school_id");

      if (!currentStudentId || !currentSchoolId) {
        setFetchError("Required identifiers are missing");
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `${BASE_URL}/student/bus/bus_fee_details.php`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              student_id: currentStudentId,
              school_id: currentSchoolId,
            }),
          }
        );

        const data = await response.json();
        console.log("API Response:", data);

        if (!response.ok || !data.success) {
          // Handle error responses
          setFetchError(
            data.message || `Request failed: ${response.statusText}`
          );
        } else {
          const busDetails = data.data?.[0]; // Assuming the backend returns an array
          if (busDetails) {
            setTransportInfo({
              transportFee: parseFloat(busDetails.bus_fee) || 0,
              validFrom: busDetails.start_date,
              validUntil: busDetails.end_date,
              paymentDueDate: busDetails.due_date,
              recordId: busDetails.id,
              fine: parseFloat(busDetails.fine) || 0, // Fine from backend
              status: busDetails.payment_status,
            });
          } else {
            setFetchError("No bus details found");
          }
        }
      } catch (error) {
        console.error("Fetch error:", error);
        setFetchError("An error occurred while retrieving bus details");
      } finally {
        setLoading(false);
      }
    };

    loadBusDetails();
  }, []);

  console.log("transport info", transportInfo);

  const isOverdue = busInfo && new Date(busInfo.dueDate) < new Date();
  const daysUntilDue = busInfo
    ? Math.ceil(
        (new Date(busInfo.dueDate) - new Date()) / (1000 * 60 * 60 * 24)
      )
    : 0;

  const gradientBg = "linear-gradient(to bottom right, teal.50, green.50)";
  const headerGradient = "linear-gradient(to right, teal.600, green.600)";
  const cardBg = useColorModeValue("white", "gray.800");

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  if (isLoading) {
    return (
      <Container centerContent>
        <Text>Loading...</Text>
      </Container>
    );
  }

  if (error) {
    return (
      <Container centerContent>
        <Text color="red.500">{error}</Text>
      </Container>
    );
  }

  // Check if all payments are "Paid"
  const allPaid = transportInfo?.status === "Paid";

  return (
    <Container
      bg={gradientBg}
      py={4}
      px={0}
      display="flex"
      justifyContent="center"
      alignItems="center"
    >
      <Card
        boxShadow="md"
        bg={`${cardBg}/90`}
        borderRadius="lg"
        w="100%"
        maxW="900px"
      >
        <Box
          position="relative"
          bgGradient={headerGradient}
          p={4}
          color="white"
          borderTopRadius="lg"
        >
          <Flex align="center" gap={2} mb={4}>
            <Box
              p={2}
              bg="whiteAlpha.200"
              borderRadius="xl"
              backdropFilter="blur(4px)"
            >
              <Icon as={FaBus} boxSize={6} />
            </Box>
            <Box>
              <Heading
                size="md"
                bgClip="text"
                bgGradient="linear(to-r, white, teal.100)"
              >
                Bus Fee Payment
              </Heading>
              <Text fontSize="xs" color="teal.100">
                Premium Transport Service
              </Text>
            </Box>
          </Flex>
          <SimpleGrid columns={3} spacing={3}>
            {[
              {
                icon: FaWallet,
                label: "Total Fee",
                value: `₹${transportInfo?.transportFee.toFixed(2)}`,
              },
              { icon: FaClock, label: "Days Left", value: daysUntilDue },
              {
                icon: FaCheckCircle,
                label: "Status",
                value: isOverdue ? "Overdue" : "Active",
              },
            ].map((stat, index) => (
              <Box
                key={index}
                p={3}
                bg="whiteAlpha.100"
                borderRadius="md"
                backdropFilter="blur(4px)"
                textAlign="center"
              >
                <Icon as={stat.icon} boxSize={5} mb={2} />
                <Text fontSize="sm" fontWeight="bold">
                  {stat.value}
                </Text>
                <Text fontSize="xs" color="teal.100">
                  {stat.label}
                </Text>
              </Box>
            ))}
          </SimpleGrid>
        </Box>

        <CardBody p={4}>
          <Box mb={6}>
            <Flex align="center" gap={4}>
              <VStack spacing={0}>
                <Icon as={FaMapMarkerAlt} boxSize={5} color="teal.600" />
                <Text fontSize="xs" color="teal.600" fontWeight="semibold">
                  {busInfo.departure}
                </Text>
              </VStack>
              <Divider borderColor="teal.400" borderWidth={1} />
              <VStack spacing={0}>
                <Icon as={FaMapMarkerAlt} boxSize={5} color="teal.600" />
                <Text fontSize="xs" color="teal.600" fontWeight="semibold">
                  {busInfo.destination}
                </Text>
              </VStack>
            </Flex>
          </Box>

          <SimpleGrid columns={2} spacing={4} mb={6}>
            {[
              { type: "Pickup", location: busInfo.pickupPoint },
              { type: "Drop", location: busInfo.dropPoint },
            ].map((point) => (
              <Box
                key={point.type}
                p={3}
                bg={cardBg}
                borderRadius="md"
                borderWidth={1}
                borderColor="teal.100"
                textAlign="center"
              >
                <Text fontSize="xs" fontWeight="medium" color="teal.600" mb={1}>
                  {point.type} Point
                </Text>
                <Flex align="center" justify="center" gap={2}>
                  <Icon as={FaMapMarkerAlt} boxSize={4} color="teal.600" />
                  <Text fontSize="sm" fontWeight="semibold">
                    {point.location}
                  </Text>
                </Flex>
              </Box>
            ))}
          </SimpleGrid>
          {!allPaid && (
            <Button
              w="full"
              bgGradient="linear(to-r, teal.600, green.600)"
              _hover={{ bgGradient: "linear(to-r, teal.700, green.700)" }}
              color="white"
              p={4}
              fontSize="sm"
              fontWeight="semibold"
              borderRadius="md"
              onClick={() => handleOpenModal()}
            >
              Pay Now ₹{transportInfo?.transportFee.toFixed(2)}
            </Button>
          )}
        </CardBody>
      </Card>
      {isModalOpen && transportInfo && (
        <Modal_Pay_bus
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          amount={transportInfo.transportFee}
          last={transportInfo.paymentDueDate}
          start={transportInfo.startDate}
          feeId={transportInfo.recordId}
        />
      )}
    </Container>
  );
};

export default BusFeeDashboard;
